/**
 * Edição da camada COMERCIAL do produto (Task 11 / D-11.2) · redesign Entrega 3.
 *
 * Só campos cuja autoridade é a Plataforma aparecem aqui — SKU, estoque,
 * preço-base, custo, fornecedor e fiscal são referência do Bling e são
 * renderizados como somente-leitura na tela de produto, com selo de origem.
 *
 * O envio entra por prop (`edit`), não por hook: assim a tela é renderizável
 * em teste sem React Query, e o caminho de gravação real (PATCH
 * `/api/catalog/platform/products/:id/commercial` → `applyCommercialUpdate`)
 * é injetado pela página. Sem o handler os campos ficam somente-leitura e a
 * tela DIZ que a gravação não está montada, em vez de oferecer um botão que
 * não grava nada.
 *
 * Redesign (apresentação + acessibilidade, régua §2 formulários):
 *  - cada campo mantém `label htmlFor`/`id` e ganha `aria-describedby` ligando a
 *    um texto de apoio (e, no erro, à mensagem de erro) + `aria-invalid`;
 *  - o erro é descrito por TEXTO ligado ao campo (nunca só cor);
 *  - uma ação primária por contexto: o botão salvar é o CTA vermelho da marca.
 * Hooks dos testes PRESERVADOS: `data-form`, `data-action`, `data-field-key` +
 * `data-editable` (nesta ordem no input), e o estado somente-leitura.
 */
import { createElement as h, type FormEvent, type ReactElement } from "react";
import { AuthorityBadge } from "./AuthorityBadge.js";
import { StatusPanel } from "./StatusPanel.js";
import type { CommercialLayerPatch } from "../api/platform-client.js";
import type { PlatformCatalogProduct } from "../api/types.js";

export interface CommercialEditHandlers {
  save: (patch: CommercialLayerPatch) => void;
  pending: boolean;
  error: Error | null;
  /** `true` depois de uma gravação bem-sucedida (feedback honesto na tela). */
  saved: boolean;
}

export interface CommercialEditFormProps {
  product: PlatformCatalogProduct;
  edit?: CommercialEditHandlers;
}

interface EditableField {
  name: Exclude<keyof CommercialLayerPatch, "published">;
  fieldKey: string;
  label: string;
  multiline?: boolean;
  hint: string;
}

const EDITABLE_FIELDS: EditableField[] = [
  { name: "commercialName", fieldKey: "commercial_name", label: "Nome comercial", hint: "Nome exibido na loja (autoria da Plataforma)." },
  { name: "shortDescription", fieldKey: "description", label: "Descrição curta", hint: "Resumo curto para listagens e cards." },
  { name: "longDescription", fieldKey: "description", label: "Descrição longa", multiline: true, hint: "Descrição completa da página do produto." },
  { name: "seoTitle", fieldKey: "seo", label: "Título de SEO", hint: "Título usado em buscadores e compartilhamento." },
  { name: "seoDescription", fieldKey: "seo", label: "Descrição de SEO", hint: "Meta description para buscadores." }
];

function valueOf(product: PlatformCatalogProduct, name: EditableField["name"]): string {
  const commercial = product.commercial;
  if (name === "commercialName") return commercial.commercialName ?? "";
  if (name === "shortDescription") return commercial.shortDescription ?? "";
  if (name === "longDescription") return commercial.longDescription ?? "";
  if (name === "seoTitle") return commercial.seoTitle ?? "";
  if (name === "seoDescription") return commercial.seoDescription ?? "";
  return "";
}

/** Só o que o formulário precisa do `FormData` — torna o submit testável. */
export interface CommercialFormValues {
  get(name: string): unknown;
}

/**
 * O caminho de submit REAL da tela, isolado do evento de DOM (que não existe
 * sem navegador). `onSubmit` abaixo só traduz o evento para cá, então o que o
 * teste exercita é exatamente o que o operador dispara.
 *
 * Devolve o patch enviado, ou `null` quando nada mudou (campo intocado não
 * pode virar linha de histórico). Autoria NÃO é montada aqui: quem editou vem
 * da sessão autenticada no servidor (ver `CommercialLayerPatch`).
 */
export function submitCommercialForm(
  product: PlatformCatalogProduct,
  values: CommercialFormValues,
  edit: CommercialEditHandlers
): CommercialLayerPatch | null {
  const patch: CommercialLayerPatch = {};
  for (const field of EDITABLE_FIELDS) {
    const raw = values.get(field.name);
    if (typeof raw !== "string") continue;
    // Só manda o que mudou: o histórico de alterações não deve registrar
    // "edição" de campo intocado.
    if (raw === valueOf(product, field.name)) continue;
    patch[field.name] = raw;
  }
  const publishedRaw = values.get("published");
  if (publishedRaw === "sim" || publishedRaw === "nao") {
    const published = publishedRaw === "sim";
    if (published !== product.commercial.published) patch.published = published;
  }
  if (!Object.keys(patch).length) return null;
  edit.save(patch);
  return patch;
}

const CONTROL =
  "w-full rounded-control border border-border-strong bg-surface px-3 py-2.5 text-base text-ink hover:border-ink " +
  "placeholder:text-muted read-only:bg-surface-soft read-only:text-body focus-visible:outline-none " +
  "aria-[invalid=true]:border-danger";

export function CommercialEditForm({ product, edit }: CommercialEditFormProps): ReactElement {
  const readOnly = !edit || edit.pending;
  const hasError = Boolean(edit?.error);
  const errorId = "commercial-error";

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!edit) return;
    submitCommercialForm(product, new FormData(event.currentTarget), edit);
  }

  return h(
    "form",
    {
      className: "commercial-form product-block flex flex-col gap-5 rounded-panel border border-border bg-surface p-5 shadow-s1",
      "data-form": "commercial",
      // Quando há erro, o form inteiro aponta para a mensagem de erro, ligando-a
      // aos campos que ela afeta (régua §2: erro ligado ao campo).
      "aria-describedby": hasError ? errorId : undefined,
      onSubmit
    },
    h(
      "div",
      { className: "flex flex-wrap items-center justify-between gap-2" },
      h("h3", { className: "text-lg font-extrabold text-ink" }, "Informações para a loja"),
      h(AuthorityBadge, { fieldKey: "commercial_name" })
    ),
    h("p", { className: "m-0 -mt-3 text-sm text-muted" }, "Texto que o cliente vê na loja e nos buscadores. Edite à vontade: cada mudança vai para o histórico."),
    h(
      "div",
      { className: "flex flex-col gap-5" },
      ...EDITABLE_FIELDS.map((field) => {
        const hintId = `commercial-${field.name}-hint`;
        const describedBy = hasError ? `${hintId} ${errorId}` : hintId;
        const common = {
          id: `commercial-${field.name}`,
          name: field.name,
          defaultValue: valueOf(product, field.name),
          readOnly,
          className: CONTROL,
          "aria-describedby": describedBy,
          "aria-invalid": hasError ? ("true" as const) : undefined,
          "data-field-key": field.fieldKey,
          "data-editable": readOnly ? "false" : "true"
        };
        return h(
          "div",
          { key: field.name, className: "commercial-form__field flex flex-col gap-1.5" },
          h(
            "label",
            { htmlFor: `commercial-${field.name}`, className: "text-sm font-extrabold text-ink" },
            field.label
          ),
          field.multiline
            ? h("textarea", { ...common, rows: 5 })
            : h("input", { ...common, type: "text" }),
          h("span", { id: hintId, className: "text-xs text-muted" }, field.hint)
        );
      })
    ),
    h(
      "div",
      { className: "commercial-form__field flex flex-col gap-1.5" },
      h("label", { htmlFor: "commercial-published", className: "text-sm font-extrabold text-ink" }, "Disponível na loja"),
      h(
        "select",
        {
          id: "commercial-published",
          name: "published",
          defaultValue: product.commercial.published ? "sim" : "nao",
          disabled: readOnly,
          className: CONTROL,
          "aria-describedby": "commercial-published-hint",
          "data-field-key": "availability",
          "data-editable": readOnly ? "false" : "true"
        },
        h("option", { value: "sim" }, "Sim, aparece na loja"),
        h("option", { value: "nao" }, "Não, fora da loja")
      ),
      h("span", { id: "commercial-published-hint", className: "text-xs text-muted" }, "Preço e estoque vêm do Bling e aparecem iguais na loja; aqui você só decide se o produto é exibido.")
    ),
    edit
      ? h(
          "button",
          {
            type: "submit",
            disabled: edit.pending,
            "data-action": "save-commercial",
            className:
              "inline-flex min-h-[44px] w-fit items-center justify-center rounded-control bg-brand px-5 py-2 " +
              "text-base font-bold text-on-brand shadow-s1 transition-colors duration-fast ease-s2 " +
              "hover:bg-brand-pressed focus-visible:outline-none disabled:opacity-70"
          },
          edit.pending ? "Gravando…" : "Salvar camada comercial"
        )
      : h(StatusPanel, {
          kind: "awaiting_source",
          title: "Gravação da camada comercial não montada nesta visualização",
          detail:
            "Os campos acima são de autoria da Plataforma e são editáveis na tela de produto; esta montagem é só leitura."
        }),
    // Erro com id estável, referenciado pelos campos via aria-describedby acima.
    edit?.error
      ? h(
          "div",
          { id: errorId },
          h(StatusPanel, { kind: "error", title: "A edição comercial não foi gravada", detail: edit.error.message })
        )
      : null,
    edit?.saved ? h(StatusPanel, { kind: "empty", title: "Camada comercial atualizada e registrada no histórico" }) : null
  );
}
